"use client";

import { Link } from "@/i18n/navigation";
import { MapPin, Music } from "lucide-react";
import { useLocale } from "next-intl";
import { FestivalSummary } from "@/lib/types";
import { formatFestivalDateRanges, isOwnImage } from "@/lib/utils";

interface FestivalCardListProps {
  festival: FestivalSummary;
  citySlug: string;
}

export function FestivalCardList({
  festival,
  citySlug,
}: FestivalCardListProps) {
  const locale = useLocale();
  const imageUrl =
    festival.image_url && isOwnImage(festival.image_url)
      ? festival.image_url
      : null;
  const dateRange = formatFestivalDateRanges(festival.day_dates, locale);
  const effectiveCitySlug = festival.city_slug ?? citySlug;
  const href = `/${effectiveCitySlug}/event/${festival.slug}`;

  return (
    <div className="group relative overflow-hidden rounded-lg border border-acid/20 bg-surface transition-all duration-300 hover:border-acid/40 hover:shadow-[0_0_30px_rgba(209,248,5,0.08)]">
      <Link
        href={href}
        rel="nofollow" prefetch={false}
        className="absolute inset-0 z-[1]"
        aria-label={`View ${festival.name}`}
      />

      {/* Same 3-column shape as event cards so the list shares one left edge */}
      <div className="lg:grid lg:min-h-[7rem] lg:grid-cols-[14rem_minmax(0,1fr)_15rem]">
        {/* Image column (desktop) */}
        <div className="hidden lg:block lg:relative lg:overflow-hidden lg:bg-neutral-900/20 lg:border-r lg:border-neutral-800/30">
          {imageUrl ? (
            <div className="absolute inset-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageUrl}
                alt=""
                className="absolute inset-0 h-full w-full object-cover blur-xl scale-125 opacity-60"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageUrl}
                  alt={festival.name}
                  className="max-h-full max-w-full object-contain drop-shadow-lg"
                />
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-acid/5 to-acid/10">
              <Music className="h-8 w-8 text-acid/30" />
            </div>
          )}
        </div>

        {/* Content column */}
        <div className="min-w-0 flex flex-col">
          {/* Header */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 lg:px-5 lg:pt-4 lg:pb-2">
            <span className="rounded bg-acid px-2 py-0.5 font-mono text-[10px] font-bold text-black uppercase tracking-wider">
              Festival
            </span>
            {dateRange && (
              <span className="font-mono text-xs font-medium text-acid/80 uppercase">
                {dateRange}
              </span>
            )}
          </div>

          {/* Festival name */}
          <div className="px-4 py-2 lg:px-5 lg:py-1">
            <h3 className="text-sm font-semibold text-white line-clamp-1">
              {festival.name}
            </h3>
          </div>

          {/* Artist count row */}
          {festival.total_artists > 0 && (
            <div className="px-4 pb-3 lg:px-5">
              <span className="font-mono text-[11px] text-neutral-400">
                {festival.total_artists} artists across{" "}
                {festival.day_dates.length} day
                {festival.day_dates.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>

        {/* Right column (desktop) */}
        <div className="hidden lg:flex lg:flex-col lg:justify-center lg:border-l lg:border-neutral-800/30 lg:bg-neutral-900/20 lg:px-5 lg:py-4">
          {/* Venue */}
          {festival.venue_name && (
            <div className="mb-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-acid/50" />
                <span className="font-mono text-[10px] text-neutral-600 uppercase tracking-wider">
                  Venue
                </span>
              </div>
              {festival.venue_slug ? (
                <Link
                  href={`/${effectiveCitySlug}/${festival.venue_slug}`}
                  onClick={(e) => e.stopPropagation()}
                  className="relative z-10 pointer-events-auto text-sm text-white hover:text-acid transition-colors line-clamp-2 leading-snug"
                >
                  {festival.venue_name}
                </Link>
              ) : (
                <span className="text-sm text-white line-clamp-2 leading-snug">
                  {festival.venue_name}
                </span>
              )}
            </div>
          )}

          {/* View lineup link */}
          <span className="font-mono text-[10px] tracking-wider text-neutral-600 group-hover:text-acid transition-colors">
            VIEW LINEUP →
          </span>
        </div>
      </div>

      {/* Mobile venue + info row */}
      <div className="flex items-center gap-3 border-t border-neutral-800/30 px-4 py-2 lg:hidden">
        {festival.venue_name && (
          <span className="flex items-center gap-1 text-xs text-neutral-400">
            <MapPin className="h-3 w-3 shrink-0 text-neutral-500" />
            {festival.venue_name}
          </span>
        )}
      </div>
    </div>
  );
}
